import React, {useContext} from "react";
import CircleLabel from "../common/CircleLabel";
import {createUseStyles, useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import {EngageAppContext} from "../../App";
import Welcome from "engage-client/components/Welcome";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import useMediaQuery from "engage-client/hooks/useMediaQuery";
import Chat from "../chat/Chat";
import {AccessoryLgWidth} from "../accessory/accessoryConstants";
import {DefaultTeamExplanation} from "engage-client/components/teams/CreateTeams";

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
	waiting: {
		height:"100%",
		padding: [0,16],
		position: "relative",
		display:"flex",
		flexDirection:"column",
		justifyContent: "space-evenly",
		alignItems: "center",
		textAlign: "center",
	},
	image: {
		width:588,
		height:447,
		border: theme.welcome.instructions.instructionBorder,
		objectFit: "contain",
		[theme.breakpoints.only("lg")]: {
			width:329,
			height:250,
		},
		[theme.breakpoints.down("md")]: {
			width:152,
			height:256,
		}
	},
	instructionsContainer: {
		display:"flex",
		position: "relative",
		width:"100%",
		maxWidth: 1000,
		[theme.breakpoints.only("lg")]: {
			maxWidth: 750,
		},
		[theme.breakpoints.down("md")]: {
			maxWidth: 500
		},
	},
	instructions: {
		flex:1,
		position:"relative",
		marginLeft: 52,
		[theme.breakpoints.down("md")]: {
			marginLeft: 24,
		},
	},
	countdownLabel: {
		position:"absolute",
		top: theme.welcome.instructions.other.countdown.text.top,
		left: theme.welcome.instructions.other.countdown.text.left,
		[theme.breakpoints.only("lg")]: {
			top: theme.welcome.instructions.large.countdown.text.top,
			left: theme.welcome.instructions.large.countdown.text.left,
		},
		[theme.breakpoints.down("md")]: {
			top: theme.welcome.instructions.small.countdown.text.top,
			left: theme.welcome.instructions.small.countdown.text.left
		},
	},
	submitLabel: {
		position:"absolute",
		top: theme.welcome.instructions.other.submit.text.top,
		[theme.breakpoints.only("lg")]: {
			top: theme.welcome.instructions.large.submit.text.top,
		},
		[theme.breakpoints.down("md")]: {
			top: theme.welcome.instructions.small.submit.text.top,
			left: theme.welcome.instructions.small.submit.text.left
		},
	},
	chatContainer: {
		position: "absolute",
		bottom: 0,
		right: 0,
		[theme.breakpoints.down("md")]: {
			left: 0
		},
		[theme.breakpoints.up("lg")]: {
			width: AccessoryLgWidth
		},
	},
	teamContainer: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
	},
	teamText: {
		marginBottom: 10,
	},
	teamDisplay: {
		padding: [10,30],
		borderRadius: 50,
		backgroundColor: props => props.teamColor,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		[theme.breakpoints.down("sm")]: {
			padding: [5,15],
		},
	},
	teamColor: {
		backgroundColor: props => props.teamLightColor,
		borderRadius: 50,
		width: 15,
		height: 15,
		marginRight: 10,
		[theme.breakpoints.down("sm")]: {
			width: 10,
			height: 10
		},
	},
	teamIcon: {
		marginRight: 10,
		height: 50,
		width: 50,
		[theme.breakpoints.down("sm")]: {
			height: 30,
			width: 30,
		}
	}
}));

export default function Waiting() {
	const {user, lobby, appState} = useContext(EngageAppContext.current);
	const theme = useTheme<Theme>();
	const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
	const largeScreen = useMediaQuery(theme.breakpoints.only("lg"));

	const teamExplanation = lobby?.teamExplanation?.length ? lobby.teamExplanation : DefaultTeamExplanation;
	const teams = lobby?.teams;
	const myTeam = user?.team && teams ? teams.find(t=>t.key === user.team) : undefined;
	const myTeamInfo = myTeam ? theme.teamInfos[myTeam.key] : undefined;
	const classes = useStyles({teamColor: myTeamInfo?.main, teamLightColor: myTeamInfo?.lightBG});

	return (
		<div className={classes.waiting} data-qa="waiting-screen">
			<Welcome title={lobby?.title ?? "Trivia Game"}>The game will begin soon.</Welcome>
			{myTeam &&
			<div className={classes.teamContainer}>
				<DisplayText variant={TextVariant.s20reg} color="white" className={classes.teamText}>
					{teamExplanation}
				</DisplayText>
				<DisplayText variant={TextVariant.s20reg} color="white" className={classes.teamText}>
					You are on:
				</DisplayText>
				<DisplayText variant={TextVariant.s34reg} fontWeight={400} color="white" className={classes.teamDisplay}>
					{theme.welcome.instructions.teamNoIcon ?
						<div className={classes.teamColor}/> :
						<img className={classes.teamIcon} src={"/assets/icons/common/team.svg"} alt={"Icon for team"}/>
					}
					{myTeam.name}
				</DisplayText>
			</div>
			}
			<div className={classes.instructionsContainer}>
				<img className={classes.image} src={smallScreen ? theme.welcome.instructions.small.src :
					theme.welcome.instructions.large.src} alt="game instructions"/>
				<div className={classes.instructions}>
					<DisplayText className={classes.countdownLabel} color="white" variant={TextVariant.s20reg} align="left">
						As time counts down, so do points. Submit your answer quickly to maximize your score.
					</DisplayText>
					<DisplayText className={classes.submitLabel} color="white" variant={TextVariant.s20reg} align="left">
						Lock in your final answer by hitting submit. There's no turning back!
					</DisplayText>
				</div>
				{smallScreen ? (
					<>
					<CircleLabel top={theme.welcome.instructions.small.countdown.label.top}
								 left={theme.welcome.instructions.small.countdown.label.left}
								 leftRadius={17} rightRadius={4} thickness={1} color={theme.welcome.instructions.circleColor ?? "white"} width={60}/>
					<CircleLabel top={theme.welcome.instructions.small.submit.label.top}
								 left={theme.welcome.instructions.small.submit.label.left}
								 leftRadius={25} rightRadius={4} thickness={1} color={theme.welcome.instructions.circleColor ?? "white"} width={78}/>
					</>
				) : largeScreen ? (
					<>
						<CircleLabel top={theme.welcome.instructions.large.countdown.label.top}
									 left={theme.welcome.instructions.large.countdown.label.left}
									 leftRadius={20} rightRadius={8} thickness={2} color={theme.welcome.instructions.circleColor ?? "white"} width={136}/>
						<CircleLabel top={theme.welcome.instructions.large.submit.label.top}
									 left={theme.welcome.instructions.large.submit.label.left}
									 leftRadius={26} rightRadius={8} thickness={2} color={theme.welcome.instructions.circleColor ?? "white"} width={230}/>
					</>
				) : (
					<>
						<CircleLabel top={theme.welcome.instructions.other.countdown.label.top}
									 left={theme.welcome.instructions.other.countdown.label.left}
									 leftRadius={46} rightRadius={8} thickness={2} color={theme.welcome.instructions.circleColor ?? "white"} width={222}/>
						<CircleLabel top={theme.welcome.instructions.other.submit.label.top}
									 left={theme.welcome.instructions.other.submit.label.left}
									 leftRadius={46} rightRadius={8} thickness={2} color={theme.welcome.instructions.circleColor ?? "white"} width={380}/>
					</>
				)}
			</div>
			{appState.common.gameOptions.showChat &&
				<Chat popup={true} className={classes.chatContainer}/>
			}
		</div>
	);
};
